// Additive JSON-LD builders for Round-5 P0-7. Extends src/lib/jsonld.ts; does not replace.
// All builders accept an active locale to drive `inLanguage`. Existing builders in jsonld.ts remain.

import { SITE_ORIGIN, type Locale } from "@/lib/seo/hreflang";

const ORIGIN = (): string => SITE_ORIGIN;

/** Site-wide Organization schema. Emit once per page (root layout). */
export function organizationFullLd(locale: Locale = "en") {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Bootpreneurs",
    url: ORIGIN(),
    logo: `${ORIGIN()}/icons/icon-512.png`,
    sameAs: [
      "https://twitter.com/bootpreneurs",
      "https://www.linkedin.com/company/bootpreneurs",
      "https://www.youtube.com/@bootpreneurs",
    ],
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        availableLanguage: ["fr", "en", "es", "pt"],
        email: "support@bootpreneurs.com",
      },
    ],
    inLanguage: locale,
  };
}

/** Course schema for a bootcamp detail page. */
export function courseFullLd(opts: {
  name: string;
  description?: string;
  slug: string;
  locale: Locale;
  image?: string;
  priceEur?: number;
  level?: string;
  startDate?: string;
}) {
  const url = `${ORIGIN()}/${opts.locale}/catalog/${opts.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    name: opts.name,
    description: opts.description,
    image: opts.image,
    url,
    inLanguage: opts.locale,
    educationalLevel: opts.level,
    provider: {
      "@type": "Organization",
      name: "Bootpreneurs",
      sameAs: ORIGIN(),
    },
    ...(opts.priceEur != null && {
      hasCourseInstance: {
        "@type": "CourseInstance",
        courseMode: "online",
        ...(opts.startDate && { startDate: opts.startDate }),
      },
      offers: {
        "@type": "Offer",
        url,
        priceCurrency: "EUR",
        price: opts.priceEur,
        availability: "https://schema.org/InStock",
      },
    }),
  };
}

/** ItemList wrapper for collection pages (catalog, blog, etc.). */
export function itemListLd(opts: {
  items: { name: string; url: string; image?: string }[];
  locale: Locale;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    inLanguage: opts.locale,
    itemListElement: opts.items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      url: it.url,
      ...(it.image && { image: it.image }),
    })),
  };
}

/** Article (blog post / case study). */
export function articleFullLd(opts: {
  headline: string;
  description?: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  author?: string;
  url: string;
  locale: Locale;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: opts.headline,
    description: opts.description,
    image: opts.image,
    inLanguage: opts.locale,
    datePublished: opts.datePublished,
    dateModified: opts.dateModified ?? opts.datePublished,
    author: opts.author ? { "@type": "Person", name: opts.author } : undefined,
    publisher: {
      "@type": "Organization",
      name: "Bootpreneurs",
      logo: { "@type": "ImageObject", url: `${ORIGIN()}/icons/icon-512.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": opts.url },
  };
}

/** Person schema for coaches / fractional execs. */
export function personLd(opts: {
  name: string;
  jobTitle?: string;
  image?: string;
  bio?: string;
  url: string;
  locale: Locale;
  knowsLanguage?: string[];
  knowsAbout?: string[];
  city?: string;
  country?: string;
  occupation?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: opts.name,
    jobTitle: opts.jobTitle,
    image: opts.image,
    description: opts.bio,
    url: opts.url,
    inLanguage: opts.locale,
    knowsLanguage: opts.knowsLanguage,
    knowsAbout: opts.knowsAbout,
    worksFor: { "@type": "Organization", name: "Bootpreneurs", sameAs: ORIGIN() },
    ...(opts.occupation && {
      hasOccupation: { "@type": "Occupation", name: opts.occupation },
    }),
    ...(opts.city && {
      address: {
        "@type": "PostalAddress",
        addressLocality: opts.city,
        addressCountry: opts.country,
      },
    }),
  };
}

/** Service schema for microservices. */
export function serviceLd(opts: {
  name: string;
  description?: string;
  url: string;
  locale: Locale;
  priceEur?: number;
  category?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: opts.name,
    description: opts.description,
    url: opts.url,
    inLanguage: opts.locale,
    serviceType: opts.category,
    provider: { "@type": "Organization", name: "Bootpreneurs", sameAs: ORIGIN() },
    ...(opts.priceEur != null && {
      offers: {
        "@type": "Offer",
        priceCurrency: "EUR",
        price: opts.priceEur,
        availability: "https://schema.org/InStock",
      },
    }),
  };
}

/** Re-export FAQPage and Breadcrumb from existing jsonld.ts to keep one import surface. */
export { faqLd as faqPageLd, breadcrumbLd } from "@/lib/jsonld";
